import { useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { fmtDate, todayAthens } from "../../../lib/format";
import { addDays, downloadFile, hours } from "../../../lib/xutil";
import { useApi } from "../../customer/hooks";
import { Field, Panel, btnGhost } from "../../../components/common/Page";
import { DateField } from "../../../components/common/DateField";
import { DaysTable, WeekNav, useWeek } from "./Timesheet";
import { LeaveList, TeamCalendar } from "./Leave";

const Ergani = () => {
  const { t } = useI18n();
  const [r, setR] = useState({ from: `${todayAthens().slice(0, 8)}01`, to: todayAthens() });
  const q = useApi("ergani", "/hr/ergani/mismatches", { date_from: r.from, date_to: r.to }, { staleTime: 0, enabled: r.from <= r.to });
  const dl = (url, name) => downloadFile(url, { date_from: r.from, date_to: r.to }, name).catch(() => toast.error(t("common.error")));
  return (
    <Panel title={t("x.hr.ergani.title")} sub={t("x.hr.ergani.sub")} testId="ergani-panel">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-40"><Field label={t("x.from")}><DateField value={r.from} onChange={(v) => v && setR((s) => ({ ...s, from: v }))} testId="ergani-from" /></Field></div>
        <div className="w-40"><Field label={t("x.to")}><DateField value={r.to} onChange={(v) => v && setR((s) => ({ ...s, to: v }))} testId="ergani-to" /></Field></div>
        <button type="button" onClick={() => dl("/hr/ergani/export.xlsx", `ergani-${r.from}-${r.to}.xlsx`)} className={btnGhost} data-testid="ergani-export"><FileSpreadsheet className="h-4 w-4 text-brand" />{t("x.hr.ergani.export")}</button>
        <button type="button" onClick={() => dl("/hr/export/timesheets.xlsx", `timesheets-${r.from}-${r.to}.xlsx`)} className={btnGhost} data-testid="timesheets-export"><FileSpreadsheet className="h-4 w-4 text-navy" />{t("x.export")}</button>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-navy">{t("x.hr.ergani.mismatches")}</h3>
      {!q.data?.length ? <p className="text-sm text-slate-500" data-testid="ergani-none">{t("x.hr.ergani.none")}</p> : (
        <ul className="mt-2 max-h-80 divide-y divide-slate-100 overflow-y-auto text-sm" data-testid="ergani-mismatches">
          {q.data.map((m) => (
            <li key={`${m.user_id}-${m.date}`} className="flex flex-wrap justify-between gap-2 py-2">
              <span className="text-navy">{m.name} · {fmtDate(m.date)} <span className="text-xs text-slate-500">({m.scheduled_start || "—"}–{m.scheduled_end || "—"} / {m.in || "—"}–{m.out || "—"})</span></span>
              <span className="flex flex-wrap gap-1">{m.issues.map((i) => <span key={i} className="rounded-full bg-rose-50 px-2 py-0.5 text-xs text-rose-700">{t(`x.hr.ergani.issues.${i}`)}</span>)}</span>
            </li>))}
        </ul>)}
    </Panel>
  );
};

export const TeamPanel = () => {
  const { t } = useI18n();
  const [week, setWeek] = useWeek();
  const [open, setOpen] = useState(null);
  const ts = useApi("teamTs", "/hr/team/timesheets", { week_start: week }, { staleTime: 0 });
  const leave = useApi("leaveTeam", "/hr/leave", { scope: "team" }, { staleTime: 0 });
  const [month, setMonth] = useState(todayAthens().slice(0, 7));
  const shiftMonth = (n) => setMonth(addDays(`${month}-15`, n * 30).slice(0, 7));
  return (
    <div className="space-y-4">
      <Panel title={t("x.hr.team.title")} action={<WeekNav week={week} setWeek={setWeek} />} testId="team-timesheets">
        {!ts.data?.people.length ? <p className="text-sm text-slate-500">{t("x.hr.team.empty")}</p> : (
          <ul className="divide-y divide-slate-100">{ts.data.people.map((p) => (
            <li key={p.user_id} className="py-2">
              <button type="button" onClick={() => setOpen(open === p.user_id ? null : p.user_id)} className="flex w-full items-center justify-between py-1 text-left text-sm" data-testid={`team-member-${p.user_id}`}>
                <span className="font-medium text-navy">{p.name}</span><span className="tabular-nums text-slate-600">{hours(p.total_min)}</span>
              </button>
              {open === p.user_id && <DaysTable days={p.days} editable onChange={ts.refetch} testId={`team-days-${p.user_id}`} />}
            </li>))}</ul>)}
      </Panel>
      <Panel title={t("x.hr.leave.team")} testId="team-leave">{leave.data && <LeaveList rows={leave.data} team onChange={leave.refetch} testId="leave-team" />}</Panel>
      <Panel title={t("x.hr.leave.calendar")} action={<div className="flex items-center gap-2"><button type="button" className={`${btnGhost} h-9 px-3`} onClick={() => shiftMonth(-1)} data-testid="cal-prev">‹</button><span className="text-sm font-medium text-navy">{month.split("-").reverse().join("/")}</span><button type="button" className={`${btnGhost} h-9 px-3`} onClick={() => shiftMonth(1)} data-testid="cal-next">›</button></div>}>
        <TeamCalendar month={month} />
      </Panel>
      <Ergani />
    </div>
  );
};
